// src/components/admin/AdminDashboard.js
import { useMemo } from 'react';
import { Package, DollarSign, TrendingUp, UserPlus, Building2 } from 'lucide-react';

export function AdminDashboard({ orders, reps = [], customers = [], onNavigate }) {
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    // Filter orders by date
    const thisMonthOrders = orders.filter(o => {
      const date = o.createdAt?.toDate?.() || new Date(o.createdAt);
      return date >= thisMonth;
    });

    const lastMonthOrders = orders.filter(o => {
      const date = o.createdAt?.toDate?.() || new Date(o.createdAt);
      return date >= lastMonth && date <= endOfLastMonth;
    });

    // Calculate revenue
    const thisMonthRevenue = thisMonthOrders.reduce((sum, o) => sum + (o.totalWithFee || o.total || 0), 0);
    const lastMonthRevenue = lastMonthOrders.reduce((sum, o) => sum + (o.totalWithFee || o.total || 0), 0);
    const totalRevenue = orders.reduce((sum, o) => sum + (o.totalWithFee || o.total || 0), 0);

    // Calculate growth
    const revenueGrowth = lastMonthRevenue > 0 
      ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100).toFixed(1)
      : 0;

    // Order stats
    const pendingOrders = orders.filter(o => o.status === 'New' || o.status === 'Processing');

    // Rep stats (includes legacy wholesalers)
    const pendingReps = reps.filter(r => !r.approved);
    const approvedReps = reps.filter(r => r.approved);

    // Customer stats
    const activeCustomers = customers.filter(c => c.active !== false);

    return {
      totalOrders: orders.length,
      thisMonthOrders: thisMonthOrders.length,
      pendingOrders: pendingOrders.length,
      totalRevenue,
      thisMonthRevenue,
      revenueGrowth,
      // Reps
      totalReps: reps.length,
      approvedReps: approvedReps.length,
      pendingReps: pendingReps.length,
      // Customers
      totalCustomers: customers.length,
      activeCustomers: activeCustomers.length
    };
  }, [orders, reps, customers]);

  // Recent orders for quick view
  const recentOrders = useMemo(() => {
    return orders.slice(0, 5);
  }, [orders]);

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Dashboard</h2>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Total Revenue */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold">${stats.totalRevenue.toFixed(2)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <DollarSign className="text-green-600" size={24} />
            </div>
          </div>
          <div className="mt-2 flex items-center text-sm">
            <TrendingUp size={16} className={stats.revenueGrowth >= 0 ? 'text-green-500' : 'text-red-500'} />
            <span className={stats.revenueGrowth >= 0 ? 'text-green-600 ml-1' : 'text-red-600 ml-1'}>
              {stats.revenueGrowth}% from last month
            </span>
          </div>
        </div>

        {/* Total Orders - Clickable */}
        <div 
          className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate?.('orders')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-bold">{stats.totalOrders}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Package className="text-purple-600" size={24} />
            </div>
          </div>
          {stats.pendingOrders > 0 && (
            <p className="mt-2 text-sm text-yellow-600">
              {stats.pendingOrders} pending
            </p>
          )}
        </div>

        {/* Reps - Clickable */}
        <div 
          className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate?.('reps')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Reps</p>
              <p className="text-2xl font-bold">{stats.approvedReps}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <UserPlus className="text-blue-600" size={24} />
            </div>
          </div>
          {stats.pendingReps > 0 && (
            <p className="mt-2 text-sm text-yellow-600">
              {stats.pendingReps} pending approval
            </p>
          )}
        </div>

        {/* Customers - Clickable */}
        <div 
          className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate?.('customers')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Customers</p>
              <p className="text-2xl font-bold">{stats.totalCustomers}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Building2 className="text-orange-600" size={24} />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            {stats.activeCustomers} active
          </p>
        </div>
      </div>

      {/* Alerts & Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Alerts */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">Alerts</h3>
          <div className="space-y-3">
            {stats.pendingReps > 0 && (
              <div 
                className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100"
                onClick={() => onNavigate?.('reps')}
              >
                <UserPlus className="text-blue-600" size={20} />
                <span className="text-sm">
                  <strong>{stats.pendingReps}</strong> rep{stats.pendingReps !== 1 && 's'} awaiting approval
                </span>
              </div>
            )}
            {stats.pendingOrders > 0 && (
              <div 
                className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg cursor-pointer hover:bg-purple-100"
                onClick={() => onNavigate?.('orders')}
              >
                <Package className="text-purple-600" size={20} />
                <span className="text-sm">
                  <strong>{stats.pendingOrders}</strong> order{stats.pendingOrders !== 1 && 's'} need processing
                </span>
              </div>
            )}
            {stats.pendingReps === 0 && stats.pendingOrders === 0 && (
              <p className="text-gray-500 text-sm">No alerts at this time</p>
            )}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4">Recent Orders</h3>
          {recentOrders.length === 0 ? (
            <p className="text-gray-500 text-sm">No orders yet</p>
          ) : (
            <div className="space-y-3">
              {recentOrders.map(order => {
                const date = order.createdAt?.toDate?.() || new Date(order.createdAt);
                return (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">#{order.id.slice(-8).toUpperCase()}</p>
                      <p className="text-xs text-gray-500">{order.userEmail}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${(order.totalWithFee || order.total || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">
                        {date.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
